// src/features/streams/components/StreamOverviewTab.tsx

import { Card, Text, Stack } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { StreamOverviewSchema, type StreamOverviewFormData } from '../schemas/StreamForm.schema';
import { useUpdateStreamMedia, useUpdateStreamSettings } from '../useStreamMutations';
import { StreamUtils } from '../StreamUtils';
import { StreamGeneralInfoForm } from './StreamGeneralInfoForm';
import { StreamControlsSection } from './StreamControlsSection';
import { StreamScheduleSection } from './schedule/StreamScheduleSection';
import { useTags } from '@/features/tag/useTags';
import { useRefIdConflict } from '@/shared/hooks/useRefIdConflict';
import type { StreamViewData } from '@/lib/types/Stream';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

interface StreamOverviewTabProps {
  stream: StreamViewData;
  /** HTTP-proxy stream: recording controls and captions are hidden (unsupported). */
  isHttpProxy?: boolean;
}

export function StreamOverviewTab({ stream, isHttpProxy = false }: StreamOverviewTabProps) {
  const mediaId = stream.media.mediaId;
  const updateMediaMutation = useUpdateStreamMedia(mediaId);
  const updateStreamMutation = useUpdateStreamSettings(mediaId);
  const { tags: allTags } = useTags();

  const { control, watch, handleSubmit, formState: { isDirty, errors }, reset } = useForm<StreamOverviewFormData>({
    resolver: zodResolver(StreamOverviewSchema),
    defaultValues: StreamUtils.transformToOverviewFormData(stream),
  });

  const originalRefId = stream.media.refId ?? '';
  const { conflict, conflictMessage, isChecking } = useRefIdConflict({
    refId: watch('refId'),
    originalRefId,
    selfScope: 'media',
    selfId: mediaId,
  });

  const onSubmit = async (data: StreamOverviewFormData) => {
    // Belt-and-braces for Enter-key submits; the Save button is already
    // disabled while a conflict is shown. Server validation stays authoritative.
    if (conflict) {
      notifications.show({
        title: 'Reference ID conflict',
        message: conflictMessage,
        color: 'orange',
      });
      return;
    }
    const refIdChanged = data.refId !== originalRefId;

    try {
      // 1. Save media fields first
      const mediaReq = StreamUtils.transformOverviewToMediaUpdateRequest(data, refIdChanged);
      await updateMediaMutation.mutateAsync(mediaReq);

      // 2. Save stream settings (only if media succeeded)
      const streamReq = StreamUtils.transformOverviewToStreamUpdateRequest(data, stream);
      await updateStreamMutation.mutateAsync(streamReq);

      // 3. Both succeeded -- reset form
      reset(data);
      notifications.show({
        title: 'Saved',
        message: `Updated "${data.title}"`,
        color: 'green',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({
        title: 'Validation Error',
        message: firstError.message,
        color: 'orange',
      });
    }
  };

  const isPending = updateMediaMutation.isPending || updateStreamMutation.isPending;

  // Recording Schedule card shows for the selected (possibly unsaved) mode AND
  // for the saved mode, so schedules stay visible while switching away unsaved.
  const watchedRecordMode = watch('recordMode');
  const showScheduleSection =
    watchedRecordMode === 'schedule' || stream.stream?.record?.recordMode === 'schedule';

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        <Text fw={600} size="lg">Stream Settings</Text>
        <FormSaveBar
          isDirty={isDirty}
          isPending={isPending}
          onDiscard={() => reset()}
          saveDisabled={conflict != null}
        />

        {/* Stream Controls Card */}
        <Card shadow="sm" padding="lg" withBorder>
          <Text fw={600} mb="md">Stream Controls</Text>
          <StreamControlsSection
            control={control}
            watch={watch}
            stream={stream}
            isHttpProxy={isHttpProxy}
          />
        </Card>

        {/* Recording Schedule Card — instant-save, outside the form's dirty state */}
        {showScheduleSection && (
          <StreamScheduleSection stream={stream} formRecordMode={watchedRecordMode} />
        )}

        {/* General Information Card */}
        <Card shadow="sm" padding="lg" withBorder>
          <Text fw={600} mb="md">General Information</Text>
          <StreamGeneralInfoForm
            control={control}
            watch={watch}
            allTags={allTags}
            refIdConflictError={conflictMessage}
            refIdChecking={isChecking}
          />
        </Card>
      </Stack>
    </form>
  );
}
